import { useEffect, useMemo } from 'react'
import {
  type Avdeling,
  ErrorMessage,
  type Filters,
  Mtds,
  OwnerId,
  SampleStatusEnum,
  type Seksjon,
  type Team,
  templates,
  useGetAvdelingerQueries,
  useGetMatricesQuery,
  useGetPlanSummariesQuery,
  useGetProductsQuery,
  useGetRegionerQuery,
  useGetSampntTypes,
  useGetSeksjonerQueries,
  useGetSubstancesQuery,
  useGetAllTeamsQuery,
  widgets
} from '../../index'
import { Form, validator } from '../form/rjsf'
import { getSampleFilterSchema } from './sampleFilterSchema'
import { sampleFilterUiSchema } from './sampleFilterUiSchema'

type SampleFilterFormProps = {
  filters: Filters<SampleStatusEnum>
  setFilters: React.Dispatch<React.SetStateAction<Filters<SampleStatusEnum>>>
  includePlanFilter?: boolean
  includeRegionFilter?: boolean
  isEnabled?: boolean
  onReadyChange?: (isReady: boolean) => void
}

type SampleFilterFormChangeData = {
  formData?: Filters<SampleStatusEnum>
}

type SampleFilterQueryStatus = {
  includePlanFilter: boolean
  includeRegionFilter: boolean
  isPendingProducts: boolean
  isPendingMatrices: boolean
  isPendingSampntTypes: boolean
  isPendingSubstances: boolean
  isPendingPlans: boolean
  isPendingRegioner: boolean
  isLoadingAvdelinger: boolean
  isLoadingSeksjoner: boolean
  isLoadingTeams: boolean
  isErrorProducts: boolean
  isErrorMatrices: boolean
  isErrorSampntTypes: boolean
  isErrorSubstances: boolean
  isErrorPlans: boolean
  isErrorRegioner: boolean
  isErrorAvdelinger: boolean
  isErrorSeksjoner: boolean
  isSuccessProducts: boolean
  isSuccessMatrices: boolean
  isSuccessSampntTypes: boolean
  isSuccessSubstances: boolean
  isSuccessPlans: boolean
}

function toDateInputValue(dateString?: string): string {
  if (!dateString) return ''
  return dateString.slice(0, 10)
}

function isInvalidDateRange(from?: string, to?: string): boolean {
  return !!from && !!to && from > to
}

function toStartOfDayIso(date?: string): string | undefined {
  return date ? new Date(`${date}T00:00:00.000Z`).toISOString() : undefined
}

function toEndOfDayIso(date?: string): string | undefined {
  return date ? new Date(`${date}T23:59:59.999Z`).toISOString() : undefined
}

function normalizeOrgFilterValue(value?: string): string | undefined {
  return value != null && value.length > 0 ? value : undefined
}

function getOwnerIdFromOrgFilters({
  regionId,
  avdelingId,
  seksjonId,
  teamId
}: Pick<
  Filters<SampleStatusEnum>,
  'regionId' | 'avdelingId' | 'seksjonId' | 'teamId'
>): string | undefined {
  if (teamId != null) return new OwnerId('team', teamId).toString()
  if (seksjonId != null) return new OwnerId('seksjon', seksjonId).toString()
  if (avdelingId != null) return new OwnerId('avdeling', avdelingId).toString()
  if (regionId != null) return new OwnerId('region', regionId).toString()
  return undefined
}

function getUpdatedOrgFilters(
  formData: Filters<SampleStatusEnum>,
  previousFilters: Filters<SampleStatusEnum>
) {
  const regionId = normalizeOrgFilterValue(formData.regionId)
  const avdelingId = normalizeOrgFilterValue(formData.avdelingId)
  const seksjonId = normalizeOrgFilterValue(formData.seksjonId)
  const teamId = normalizeOrgFilterValue(formData.teamId)
  const regionChanged =
    regionId !== normalizeOrgFilterValue(previousFilters.regionId)
  const avdelingChanged =
    avdelingId !== normalizeOrgFilterValue(previousFilters.avdelingId)
  const seksjonChanged =
    seksjonId !== normalizeOrgFilterValue(previousFilters.seksjonId)
  const clearSeksjon = regionChanged || avdelingChanged
  const clearTeam = clearSeksjon || seksjonChanged

  return {
    regionId,
    avdelingId: regionChanged ? undefined : avdelingId,
    seksjonId: clearSeksjon ? undefined : seksjonId,
    teamId: clearTeam ? undefined : teamId
  }
}

function getNextFilters(
  formData: Filters<SampleStatusEnum>,
  previousFilters: Filters<SampleStatusEnum>
) {
  const orgFilters = getUpdatedOrgFilters(formData, previousFilters)
  const withoutEmptyIds = (ids?: string[]) => ids?.filter(Boolean)

  return {
    ...previousFilters,
    ...formData,
    ...orgFilters,
    ownerId: getOwnerIdFromOrgFilters(orgFilters),
    planIds: withoutEmptyIds(formData.planIds),
    productIds: withoutEmptyIds(formData.productIds),
    matrixIds: withoutEmptyIds(formData.matrixIds),
    substanceIds: withoutEmptyIds(formData.substanceIds),
    sampntTypeIds: withoutEmptyIds(formData.sampntTypeIds),
    fromDate: toStartOfDayIso(formData.fromDate),
    toDate: toEndOfDayIso(formData.toDate)
  }
}

function handleFilterFormChange(
  data: SampleFilterFormChangeData,
  setFilters: React.Dispatch<React.SetStateAction<Filters<SampleStatusEnum>>>
) {
  const formData = data.formData

  if (formData == null) return

  const fromDate = formData.fromDate
  const toDate = formData.toDate

  if (isInvalidDateRange(fromDate, toDate)) {
    Mtds.toast.warning('Fra dato kan ikke være senere enn til dato')
    return
  }

  setFilters((previousFilters) => getNextFilters(formData, previousFilters))
}

function isSampleFilterPending({
  includePlanFilter,
  includeRegionFilter,
  isPendingProducts,
  isPendingMatrices,
  isPendingSampntTypes,
  isPendingSubstances,
  isPendingPlans,
  isPendingRegioner,
  isLoadingAvdelinger,
  isLoadingSeksjoner,
  isLoadingTeams
}: SampleFilterQueryStatus) {
  const basePending = [
    isPendingProducts,
    isPendingMatrices,
    isPendingSampntTypes,
    isPendingSubstances
  ].some(Boolean)
  const regionPending = [
    isPendingRegioner,
    isLoadingAvdelinger,
    isLoadingSeksjoner,
    isLoadingTeams
  ].some(Boolean)

  return (
    basePending ||
    (includePlanFilter && isPendingPlans) ||
    (includeRegionFilter && regionPending)
  )
}

function isSampleFilterError({
  includePlanFilter,
  includeRegionFilter,
  isErrorProducts,
  isErrorMatrices,
  isErrorSampntTypes,
  isErrorSubstances,
  isErrorPlans,
  isErrorRegioner,
  isErrorAvdelinger,
  isErrorSeksjoner
}: SampleFilterQueryStatus) {
  const baseError = [
    isErrorProducts,
    isErrorMatrices,
    isErrorSampntTypes,
    isErrorSubstances
  ].some(Boolean)
  const regionError = [
    isErrorRegioner,
    isErrorAvdelinger,
    isErrorSeksjoner
  ].some(Boolean)

  return (
    baseError ||
    (includePlanFilter && isErrorPlans) ||
    (includeRegionFilter && regionError)
  )
}

function isSampleFilterSuccess({
  includePlanFilter,
  includeRegionFilter,
  isPendingRegioner,
  isLoadingAvdelinger,
  isLoadingSeksjoner,
  isLoadingTeams,
  isSuccessProducts,
  isSuccessMatrices,
  isSuccessSampntTypes,
  isSuccessSubstances,
  isSuccessPlans
}: SampleFilterQueryStatus) {
  const baseSuccess = [
    isSuccessProducts,
    isSuccessMatrices,
    isSuccessSampntTypes,
    isSuccessSubstances
  ].every(Boolean)
  const regionLoading = [
    isPendingRegioner,
    isLoadingAvdelinger,
    isLoadingSeksjoner,
    isLoadingTeams
  ].some(Boolean)

  return (
    baseSuccess &&
    (!includePlanFilter || isSuccessPlans) &&
    (!includeRegionFilter || !regionLoading)
  )
}

const getOrgFilteredSeksjoner = (
  seksjoner: Seksjon[],
  avdelinger: Avdeling[],
  regionId?: string,
  avdelingId?: string
) => {
  if (normalizeOrgFilterValue(avdelingId) != null) {
    return seksjoner.filter((seksjon) => seksjon.avdelingId === avdelingId)
  }
  if (normalizeOrgFilterValue(regionId) != null) {
    const avdelingIds = new Set(
      avdelinger
        .filter((avdeling) => avdeling.regionId === regionId)
        .map((avdeling) => avdeling.id)
    )
    return seksjoner.filter((seksjon) =>
      seksjon.avdelingId == null
        ? seksjon.regionId === regionId
        : avdelingIds.has(seksjon.avdelingId)
    )
  }

  return seksjoner
}

const getOrgFilteredTeams = (
  teams: Team[],
  seksjoner: Seksjon[],
  avdelinger: Avdeling[],
  regionId?: string,
  avdelingId?: string,
  seksjonId?: string
) => {
  if (normalizeOrgFilterValue(seksjonId) != null) {
    return teams.filter((team) => team.seksjonId === seksjonId)
  }

  if (normalizeOrgFilterValue(avdelingId) != null) {
    const seksjonerForAvdeling = seksjoner.filter(
      (seksjon) => seksjon.avdelingId === avdelingId
    )
    const seksjonIds = new Set(
      seksjonerForAvdeling.map((seksjon) => seksjon.id)
    )
    return teams.filter(
      (team) =>
        team.avdelingId === avdelingId ||
        (team.seksjonId != null && seksjonIds.has(team.seksjonId))
    )
  }

  const filteredSeksjoner = getOrgFilteredSeksjoner(
    seksjoner,
    avdelinger,
    regionId,
    avdelingId
  )
  const avdelingIds = new Set(
    avdelinger
      .filter((avdeling) => {
        if (normalizeOrgFilterValue(regionId) == null) return true
        return avdeling.regionId === regionId
      })
      .map((avdeling) => avdeling.id)
  )
  const seksjonIds = new Set(filteredSeksjoner.map((seksjon) => seksjon.id))
  return teams.filter(
    (team) =>
      (team.seksjonId != null && seksjonIds.has(team.seksjonId)) ||
      (team.avdelingId != null && avdelingIds.has(team.avdelingId))
  )
}

export const SampleFilterForm = ({
  filters,
  setFilters,
  includePlanFilter = false,
  includeRegionFilter = false,
  isEnabled = true,
  onReadyChange
}: SampleFilterFormProps) => {
  const {
    data: products,
    isPending: isPendingProducts,
    isError: isErrorProducts,
    isSuccess: isSuccessProducts,
    error: productsError
  } = useGetProductsQuery(isEnabled)
  const {
    data: matrices,
    isPending: isPendingMatrices,
    isError: isErrorMatrices,
    isSuccess: isSuccessMatrices,
    error: matricesError
  } = useGetMatricesQuery(isEnabled)
  const {
    data: sampntTypes,
    isPending: isPendingSampntTypes,
    isError: isErrorSampntTypes,
    isSuccess: isSuccessSampntTypes,
    error: sampntTypesError
  } = useGetSampntTypes(isEnabled)
  const {
    data: substances,
    isPending: isPendingSubstances,
    isError: isErrorSubstances,
    isSuccess: isSuccessSubstances,
    error: substancesError
  } = useGetSubstancesQuery(isEnabled)
  const {
    data: plans,
    isPending: isPendingPlans,
    isError: isErrorPlans,
    isSuccess: isSuccessPlans,
    error: plansError
  } = useGetPlanSummariesQuery(isEnabled && includePlanFilter)
  const {
    data: regioner,
    isPending: isPendingRegioner,
    isError: isErrorRegioner,
    isSuccess: isSuccessRegioner,
    error: regionerError
  } = useGetRegionerQuery(isEnabled && includeRegionFilter)
  const regionIds = useMemo(
    () => (includeRegionFilter ? (regioner ?? []).map(({ id }) => id) : []),
    [includeRegionFilter, regioner]
  )
  const {
    data: avdelinger,
    isLoading: isLoadingAvdelinger,
    isError: isErrorAvdelinger,
    error: avdelingerError
  } = useGetAvdelingerQueries(
    isEnabled && includeRegionFilter && isSuccessRegioner ? regionIds : []
  )
  const avdelingOwnerIds = useMemo(
    () =>
      includeRegionFilter
        ? avdelinger.map(({ id }) => new OwnerId('avdeling', id))
        : [],
    [includeRegionFilter, avdelinger]
  )
  const {
    data: seksjoner,
    isLoading: isLoadingSeksjoner,
    isError: isErrorSeksjoner,
    error: seksjonerError
  } = useGetSeksjonerQueries(
    isEnabled && includeRegionFilter ? avdelingOwnerIds : []
  )
  const { data: teams = [], isLoading: isLoadingTeams } = useGetAllTeamsQuery(
    isEnabled && includeRegionFilter
  )
  const filteredAvdelinger = useMemo(
    () =>
      normalizeOrgFilterValue(filters.regionId) == null
        ? avdelinger
        : avdelinger.filter(
            (avdeling) => avdeling.regionId === filters.regionId
          ),
    [avdelinger, filters.regionId]
  )
  const filteredSeksjoner = useMemo(
    () =>
      getOrgFilteredSeksjoner(
        seksjoner,
        avdelinger,
        filters.regionId,
        filters.avdelingId
      ),
    [avdelinger, filters.avdelingId, filters.regionId, seksjoner]
  )
  const filteredTeams = useMemo(
    () =>
      getOrgFilteredTeams(
        teams,
        seksjoner,
        avdelinger,
        filters.regionId,
        filters.avdelingId,
        filters.seksjonId
      ),
    [
      avdelinger,
      filters.avdelingId,
      filters.regionId,
      filters.seksjonId,
      seksjoner,
      teams
    ]
  )

  const queryStatus = {
    includePlanFilter,
    includeRegionFilter,
    isPendingProducts,
    isPendingMatrices,
    isPendingSampntTypes,
    isPendingSubstances,
    isPendingPlans,
    isPendingRegioner,
    isLoadingAvdelinger,
    isLoadingSeksjoner,
    isLoadingTeams,
    isErrorProducts,
    isErrorMatrices,
    isErrorSampntTypes,
    isErrorSubstances,
    isErrorPlans,
    isErrorRegioner,
    isErrorAvdelinger,
    isErrorSeksjoner,
    isSuccessProducts,
    isSuccessMatrices,
    isSuccessSampntTypes,
    isSuccessSubstances,
    isSuccessPlans
  }
  const isPending = isSampleFilterPending(queryStatus)
  const isError = isSampleFilterError(queryStatus)
  const isSuccess = isSampleFilterSuccess(queryStatus)
  const error =
    productsError ??
    matricesError ??
    sampntTypesError ??
    substancesError ??
    plansError ??
    regionerError ??
    avdelingerError ??
    seksjonerError

  useEffect(() => {
    onReadyChange?.(isSuccess)
  }, [isSuccess, onReadyChange])

  return (
    <Mtds.Grid>
      {isPending && !isError && (
        <Mtds.Spinner className='mt-8'>Henter filterdata...</Mtds.Spinner>
      )}
      {isError && <ErrorMessage error={error} />}
      {isSuccess && (
        <Form
          noHtml5Validate
          schema={getSampleFilterSchema({
            products: products ?? [],
            matrices: matrices ?? [],
            sampntTypes: sampntTypes ?? [],
            substances: substances ?? [],
            plans: plans ?? [],
            regioner: regioner ?? [],
            avdelinger: filteredAvdelinger ?? [],
            seksjoner: filteredSeksjoner ?? [],
            teams: filteredTeams ?? [],
            includePlanFilter,
            includeRegionFilter
          })}
          uiSchema={sampleFilterUiSchema}
          widgets={widgets}
          templates={templates}
          showErrorList={false}
          validator={validator}
          formData={{
            ...filters,
            fromDate: toDateInputValue(filters.fromDate),
            toDate: toDateInputValue(filters.toDate)
          }}
          onChange={(data) => handleFilterFormChange(data, setFilters)}
          experimental_defaultFormStateBehavior={{
            constAsDefaults: 'skipOneOf'
          }}
        >
          <></>
        </Form>
      )}
    </Mtds.Grid>
  )
}
